import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { useAuth } from '../../../auth/AuthProvider';
import { permissions } from '../../../auth/permissions';
import { useConversations } from '../api/useConversations';
import { IncrementalListFooter } from '../../../components/ui/IncrementalListFooter';
import { useIncrementalItems } from '../../../components/ui/useIncrementalItems';
import { STANDARD_LIST_BATCH_SIZE } from '../../../components/ui/list-batch-sizes';

export function ConversationsPage() {
  const { machineId = '' } = useParams();
  const auth = useAuth();
  const conversations = useConversations(machineId, auth.can(permissions.conversationRead));
  const scope = `${auth.activeCustomerId}:${machineId}`;
  const [searchState, setSearchState] = useState({ scope, value: '' });
  const search = searchState.scope === scope ? searchState.value : '';
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const availableConversations = conversations.data?.conversations.filter((item) => item.turn_count !== 0) ?? [];
  const matchingConversations = normalizedSearch
    ? availableConversations.filter((item) => (item.title || 'Machine conversation').toLocaleLowerCase().includes(normalizedSearch))
    : availableConversations;
  const pager = useIncrementalItems(matchingConversations, STANDARD_LIST_BATCH_SIZE, `${scope}:${normalizedSearch}`);

  if (!auth.can(permissions.conversationRead)) return <Navigate to={`/machines/${machineId}`} replace />;

  return (
    <section className="r42-page r42-page--wide r42-conversation-history-page">
      <header className="r42-page-header">
        <div><span className="r42-eyebrow">Machine workspace</span><h1>Conversations</h1><p>Resume previous questions for this machine or start a new line of inquiry.</p></div>
        {auth.can(permissions.conversationCreate) ? <Link className="btn r42-btn-primary" to={`/machines/${machineId}/conversations/new`}><i className="bi bi-plus-lg" aria-hidden="true" /> New conversation</Link> : null}
      </header>

      {availableConversations.length > 0 ? (
        <div className="r42-list-toolbar">
          <label className="r42-search">
            <i className="bi bi-search" aria-hidden="true" />
            <span className="visually-hidden">Search conversations</span>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearchState({ scope, value: event.target.value })}
              placeholder="Search conversations..."
            />
          </label>
        </div>
      ) : null}
      {conversations.isLoading ? <div className="r42-inline-state">Loading conversations…</div> : null}
      {conversations.isError ? <div className="r42-inline-state r42-inline-state--error"><span>Route42 couldn't load conversation history.</span><button className="btn btn-sm btn-outline-secondary" onClick={() => void conversations.refetch()}>Try again</button></div> : null}
      {conversations.data && availableConversations.length === 0 ? (
        <div className="r42-document-empty r42-empty-state"><span className="r42-eyebrow">No conversations yet</span><h2>Ask the first question.</h2><p>Route42 creates a conversation when you submit a question, so this history stays free of empty drafts.</p><Link className="btn r42-btn-primary" to={`/machines/${machineId}/conversations/new`}>Start a conversation</Link></div>
      ) : null}
      {availableConversations.length > 0 && matchingConversations.length === 0 ? (
        <div className="r42-inline-state" role="status">
          <span>No conversations match your search.</span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setSearchState({ scope, value: '' })}>Clear search</button>
        </div>
      ) : null}
      {matchingConversations.length > 0 ? (
        <div className="r42-conversation-history-list">
          {pager.visible.map((conversation) => (
            <Link key={conversation.conversation_id} className="r42-conversation-history-row" to={`/machines/${machineId}/conversations/${conversation.conversation_id}`}>
              <span className="r42-conversation-history-icon"><i className="bi bi-chat-left-text" aria-hidden="true" /></span>
              <span className="r42-conversation-history-body"><strong>{conversation.title || 'Machine conversation'}</strong><small>{formatDate(conversation.updated_at ?? conversation.created_at ?? null)}</small></span>
              <i className="bi bi-chevron-right" aria-hidden="true" />
            </Link>
          ))}
        </div>
      ) : null}
      <IncrementalListFooter label="conversations" loaded={pager.loaded} total={pager.total} hasMore={pager.hasMore} onLoadMore={pager.loadMore} />
    </section>
  );
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? '—' : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}
